/**
 * 产品评价相关接口
 * 对应 Home端 - 产品评价模块
 */

import { http } from "@/http/axios";
import { type OrderProductType } from "./order";

// ==================== 类型定义 ====================

/** 产品类型枚举值（后端数字） */
export const ProductTypeMap = {
  1: "hotel",
  2: "tour",
  3: "car",
  4: "guide",
} as const;

/** 评价详情 */
export interface CommentDetail {
  id: number;
  order_id: number;
  product_id: number;
  product_type: string;
  product_score: number;    // 1-5星
  service_score: number;    // 1-5星
  comment: string;
  pics: string[];
  tags: string[];
  nickname: string;
  avatar: string;
  add_time: string;
  is_reply: number;          // 是否已回复 0/1
  merchant_reply_content?: string;
  merchant_reply_time?: string;
}

/** 评价列表项 */
export interface CommentListItem extends Omit<CommentDetail, "order_id"> {
  order_id: number;
}

/** 评分统计 */
export interface ScoreStats {
  "1": number; // 1星数量
  "2": number;
  "3": number;
  "4": number;
  "5": number;
}

/** 评价列表响应 */
export interface CommentListResponse {
  code: number;
  message: string;
  data: {
    list: CommentListItem[];
    score_stats: ScoreStats;
    total_score: number;      // 平均分，如 4.6
    total_count: number;      // 总评价数
    pagination: {
      page: number;
      limit: number;
      total: number;
      total_pages: number;
    };
  };
}

/** 创建评价参数 */
export interface CreateCommentParams {
  order_id: number;
  product_id: number;
  product_type: "1" | "2" | "3" | "4";   // 1酒店 2线路 3租车 4导游
  product_score: number;       // 1-5
  service_score: number;       // 1-5
  comment: string;
  pics?: string[];
  tags?: string[];
}

/** 创建评价响应 */
export interface CreateCommentResponse {
  code: number;
  message: string;
  data: {
    comment_id: number;
    product_score: number;
    service_score: number;
    comment: string;
    pics: string[];
    tags: string[];
    add_time: string;
  };
}

/** 评价详情响应 */
export interface CommentDetailResponse {
  code: number;
  message: string;
  data: CommentDetail;
}

// ==================== 接口请求 ====================

/**
 * 获取产品评价列表
 * GET /home/comment/list
 * 需认证：Header Authorization
 */
export function getCommentList(params: { product_id: number; product_type: string; page?: number; limit?: number; score?: number }) {
  return http.get<CommentListResponse>("/home/comment/list", { params });
}

/**
 * 获取评价详情
 * GET /home/comment/detail
 * 需认证：Header Authorization
 */
export function getCommentDetail(params: { order_id: number; product_type: string }) {
  return http.get<CommentDetailResponse>("/home/comment/detail", { params });
}

/**
 * 创建评价
 * POST /home/comment/create
 * 需认证：Header Authorization (form-data)
 */
export function createComment(params: CreateCommentParams) {
  return http.post<CreateCommentResponse>("/home/comment/create", params, {
    headers: { "Content-Type": "multipart/form-data" },
    transformRequest: [(data) => {
      const form = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((item) => form.append(`${key}[]`, item));
        } else if (value !== undefined && value !== null) {
          form.append(key, String(value));
        }
      });
      return form;
    }],
  });
}
